import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaColumns,
  FaDollarSign,
  FaExclamationTriangle,
  FaFileDownload,
  FaFileInvoiceDollar,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import { utils, writeFileXLSX } from "xlsx";
import { PageV2 } from "containers/PageV2";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { DropdownSelect } from "components/DropdownSelect";
import { CustomInputModal } from "components/CustomInputModal";
import { CustomDatePicker } from "components/DatePicker";
import { MaisFiltros } from "components/ButtonModal";
import { PrimaryButton } from "components/Button/style";
import { ModalConfigColunas } from "components/ModalConfigColunas";
import { useConfigColunas } from "components/ModalConfigColunas/useConfigColunas";
import { ModalBaixaManual } from "components/ModalBaixaManual";
import { LoadingScreen } from "components/LoadingScreen";
import usePermissoes from "hooks/usePermissoes";
import { toastAtencao, toastErro, toastSucesso } from "components/Toast";
import { converterUtcParaBr } from "../utils/data";
import { formatarDinheiro } from "_utils/Money";
import { StatusItemPagamento } from "../containers/StatusItemPagamento";
import { useSalvarLote } from "./hooks/useSalvarLote";
import { useLoteProcessos } from "./hooks/useLoteProcessos";
import { useAbrirProcesso } from "./hooks/useAbrirProcesso";
import { useBaixaManualTaxa } from "./hooks/useBaixaManualTaxa";
import { useReprocessarTaxa } from "./hooks/useReprocessarTaxa";
import { usePagarTaxa } from "./hooks/usePagarTaxa";
import { useBaixarComprovante } from "./hooks/useBaixarComprovante";
import { tipoPagamentoOptions } from "../constants/optionsFiltros";
import { PERMISSAO_TAXA } from "../constants/permissoes";
import {
  StatusPagamentoTaxa,
  StatusPagamentoTaxaLabel,
  StatusPagamentoTaxaTituloPopover,
  statusPagamentoTaxaOptions,
} from "../containers/StatusItemPagamento/enums/StatusPagamentoTaxa.enum";
import { PopoverObservacao } from "./containers/PopoverObservacao";
import { PopoverDebitosAdicionais } from "./containers/PopoverDebitosAdicionais";
import { ModalConfirmarPagamento } from "./containers/ModalConfirmarPagamento";
import { PopoverHover } from "components/PopoverHover";
import {
  CHAVE_CONFIG_COLUNAS_LOTE,
  colunaLabelsLote,
  defaultConfigColunasLote,
} from "./constants/configColunasLote";
import {
  IProcessoTaxa,
  ModalSelecionarProcessos,
  semOrigemVeiculo,
} from "./ModalSelecionarProcessos";
import {
  AcoesContainer,
  AcoesFiltroContainer,
  Button,
  Card,
  DatePickerContainer,
  FormContainer,
  Header,
  HeaderBotoes,
  InputBuscaProcesso,
  InputContainer,
  InputContainerMaisFiltros,
  Placeholder,
  TabelaContainer,
  Title,
} from "./style";

interface IFiltroCriarLote {
  idEmpresa: number | null;
  dadosVeiculos: string[];
  status: number | null;
  periodoPagamento: [Date, Date] | null;
  processo: string;
  clienteFinal: string | null;
  tipoPagamento: string | null;
}

const VALORES_INICIAIS: IFiltroCriarLote = {
  idEmpresa: null,
  dadosVeiculos: [],
  status: null,
  periodoPagamento: null,
  processo: "",
  clienteFinal: null,
  tipoPagamento: null,
};

export function CriarLotePagamentoTaxa() {
  const navigate = useNavigate();
  const { id } = useParams();
  const idLote = id && !isNaN(Number(id)) ? Number(id) : null;
  const modoVisualizacao = idLote != null;

  const { salvando, salvar, atualizar } = useSalvarLote();
  const { carregar } = useLoteProcessos();
  const { redirecionarProcesso } = useAbrirProcesso();
  const { salvando: salvandoBaixa, pagarManual, cancelar } =
    useBaixaManualTaxa();
  const { loadingReprocessar, reprocessar, reprocessarLote } =
    useReprocessarTaxa();
  const { pagando, pagarLote } = usePagarTaxa();
  const { baixar: baixarComprovante } = useBaixarComprovante();
  const { listaPermissoes } = usePermissoes();

  const podePagar =
    !listaPermissoes?.length ||
    listaPermissoes.some(
      (item) => item.nome_funcionalidade === PERMISSAO_TAXA.PAGAR_LOTE,
    );

  const titulo = modoVisualizacao
    ? `Lote de pagamento ${idLote}`
    : "Criar novo lote de pagamento";

  const { handleSubmit, control, setValue } = useForm<IFiltroCriarLote>({
    defaultValues: VALORES_INICIAIS,
  });

  const [inicioPagamento, setInicioPagamento] = useState<Date | undefined>();
  const [fimPagamento, setFimPagamento] = useState<Date | undefined>();

  const [showModalProcessos, setShowModalProcessos] = useState(false);
  const [processosLote, setProcessosLote] = useState<IProcessoTaxa[]>([]);
  const [idsOriginais, setIdsOriginais] = useState<number[]>([]);
  const [filtrosAplicados, setFiltrosAplicados] = useState<
    (IFiltroCriarLote & { inicioPagamento?: Date; fimPagamento?: Date }) | null
  >(null);
  const [showModalColunas, setShowModalColunas] = useState(false);
  const { configColunas, isVisible, salvarConfigColunas } = useConfigColunas(
    CHAVE_CONFIG_COLUNAS_LOTE,
    defaultConfigColunasLote,
  );
  const [selecionadosLote, setSelecionadosLote] = useState<IProcessoTaxa[]>([]);
  const [limparSelecaoLote, setLimparSelecaoLote] = useState(false);
  const [showModalBaixa, setShowModalBaixa] = useState(false);
  const [processoBaixa, setProcessoBaixa] = useState<IProcessoTaxa | null>(null);
  const [showModalPagar, setShowModalPagar] = useState(false);
  const [processosParaPagar, setProcessosParaPagar] = useState<IProcessoTaxa[]>([]);
  const [pagamentoSucesso, setPagamentoSucesso] = useState(false);
  const [recarregando, setRecarregando] = useState(false);
  const [filtrando, setFiltrando] = useState(false);
  const [versaoBuscaLote, setVersaoBuscaLote] = useState(0);
  const [carregandoProcessos, setCarregandoProcessos] = useState(modoVisualizacao);
  const pularCargaRef = useRef<number | null>(null);
  const selecionadosPagaveis = useMemo(
    () =>
      selecionadosLote.filter(
        (p) =>
          ![
            StatusPagamentoTaxa.PAGO,
            StatusPagamentoTaxa.MANUAL,
            StatusPagamentoTaxa.CANCELADO,
          ].includes(p.statusPagamento ?? StatusPagamentoTaxa.PENDENTE),
      ),
    [selecionadosLote],
  );

  const possuiItensNoLote = processosLote.length > 0;
  const idsNoLote = useMemo(
    () => processosLote.map((p) => p.idProcesso),
    [processosLote],
  );

  useEffect(() => {
    if (idLote == null) return;
    if (pularCargaRef.current === idLote) {
      pularCargaRef.current = null;
      return;
    }
    setCarregandoProcessos(true);
    carregar(idLote)
      .then((itens) => {
        setProcessosLote(itens);
        setIdsOriginais(itens.map((p) => p.idProcesso));
      })
      .finally(() => setCarregandoProcessos(false));
  }, [idLote, carregar]);

  const sinalizarFiltro = () => {
    setFiltrando(true);
    setTimeout(() => setFiltrando(false), 500);
  };

  const handleBuscar = (dados: IFiltroCriarLote) => {
    setFiltrosAplicados({ ...dados, inicioPagamento, fimPagamento });
    setVersaoBuscaLote((v) => v + 1);
    sinalizarFiltro();
  };

  const processosFiltrados = useMemo(() => {
    const f = filtrosAplicados;
    if (!f) return processosLote;

    return processosLote.filter((p) => {
      if (f.idEmpresa && Number(p.idEmpresa) !== Number(f.idEmpresa)) return false;
      if (
        f.status != null &&
        String(f.status) !== "" &&
        Number(p.statusPagamento ?? 0) !== Number(f.status)
      )
        return false;
      if (f.clienteFinal && p.clienteFinal !== f.clienteFinal) return false;
      if (f.tipoPagamento && p.tipoPgto !== f.tipoPagamento) return false;
      if (f.processo?.trim() && !String(p.idProcesso).includes(f.processo.trim()))
        return false;

      if (f.dadosVeiculos?.length) {
        const alvo = f.dadosVeiculos.map((v) => v.trim().toUpperCase());
        const bateu =
          (p.placa && alvo.includes(p.placa.trim().toUpperCase())) ||
          (p.renavam && alvo.includes(p.renavam.trim().toUpperCase())) ||
          (p.chassi && alvo.includes(p.chassi.trim().toUpperCase()));
        if (!bateu) return false;
      }

      if (f.inicioPagamento && f.fimPagamento) {
        if (!p.dataHoraPagamento) return false;
        const d = new Date(p.dataHoraPagamento).getTime();
        const ini = new Date(f.inicioPagamento);
        ini.setHours(0, 0, 0, 0);
        const fim = new Date(f.fimPagamento);
        fim.setHours(23, 59, 59, 999);
        if (d < ini.getTime() || d > fim.getTime()) return false;
      }

      return true;
    });
  }, [processosLote, filtrosAplicados]);

  const [ordenacao, setOrdenacao] = useState<{
    campo: keyof IProcessoTaxa;
    dir: "asc" | "desc";
  } | null>(null);

  const handleOrdenar = (column: any, dir: string) => {
    if (column?.sortField) {
      setOrdenacao({
        campo: column.sortField,
        dir: dir === "asc" ? "asc" : "desc",
      });
    }
  };

  const processosOrdenados = useMemo(() => {
    if (!ordenacao) return processosFiltrados;
    const { campo, dir } = ordenacao;
    const fator = dir === "asc" ? 1 : -1;
    return [...processosFiltrados].sort((a, b) => {
      const va = a[campo];
      const vb = b[campo];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * fator;
      }
      return (
        String(va).localeCompare(String(vb), "pt-BR", { numeric: true }) * fator
      );
    });
  }, [processosFiltrados, ordenacao]);

  const dadosTabelaLote = useMemo(
    () =>
      processosOrdenados.map((p) => ({
        ...p,
        selectable: ![
          StatusPagamentoTaxa.PAGO,
          StatusPagamentoTaxa.MANUAL,
        ].includes(p.statusPagamento ?? 0),
      })),
    [processosOrdenados],
  );

  const clientesFinaisOptions = useMemo(() => {
    const nomes = Array.from(
      new Set(processosLote.map((p) => p.clienteFinal).filter(Boolean)),
    ) as string[];
    return nomes.map((nome) => ({ value: nome, label: nome }));
  }, [processosLote]);

  const empresasLoteOptions = useMemo(() => {
    const mapa = new Map<number, string>();
    processosLote.forEach((p) => {
      if (p.idEmpresa != null && p.empresa) mapa.set(p.idEmpresa, p.empresa);
    });
    return Array.from(mapa, ([value, label]) => ({ value, label }));
  }, [processosLote]);

  const loteFinalizado = useMemo(
    () =>
      modoVisualizacao &&
      processosLote.length > 0 &&
      processosLote.every((p) =>
        [
          StatusPagamentoTaxa.PAGO,
          StatusPagamentoTaxa.MANUAL,
          StatusPagamentoTaxa.CANCELADO,
        ].includes(p.statusPagamento ?? StatusPagamentoTaxa.PENDENTE),
      ),
    [modoVisualizacao, processosLote],
  );

  const handleLimpar = () => {
    setValue("idEmpresa", null);
    setValue("dadosVeiculos", []);
    setValue("status", null);
    setValue("periodoPagamento", null);
    setValue("processo", "");
    setValue("clienteFinal", null);
    setValue("tipoPagamento", null);
    setInicioPagamento(undefined);
    setFimPagamento(undefined);
    setFiltrosAplicados(null);
    setVersaoBuscaLote((v) => v + 1);
    sinalizarFiltro();
  };

  const handleCancelar = () => {
    navigate("/financeiro/pagamento-taxa-sp/lote");
  };

  const handleSelecionarProcessos = () => {
    setShowModalProcessos(true);
  };

  const persistirLote = async (lista: IProcessoTaxa[]): Promise<boolean> => {
    if (salvando) return false;
    const ids = lista.map((p) => p.idProcesso);

    if (idLote == null) {
      if (!ids.length) return false;
      try {
        const resultado = await salvar(ids);
        const novoIdLote = resultado?.id;
        toastSucesso("Lote criado com sucesso.");
        if (novoIdLote) {
          setIdsOriginais(ids);
          pularCargaRef.current = novoIdLote;
          navigate(`/financeiro/pagamento-taxa-sp/lote/criar/${novoIdLote}`, {
            replace: true,
          });
        }
        return true;
      } catch {
        toastErro("Não foi possível criar o lote. Tente novamente.");
        return false;
      }
    }

    try {
      await atualizar(idLote, ids);
      setIdsOriginais(ids);
      toastSucesso("Lote atualizado com sucesso.");
      return true;
    } catch {
      toastErro("Não foi possível atualizar o lote. Tente novamente.");
      return false;
    }
  };

  const handleAdicionarProcessos = async (processos: IProcessoTaxa[]) => {
    const idsExistentes = new Set(processosLote.map((p) => p.idProcesso));
    const novos = processos.filter((p) => !idsExistentes.has(p.idProcesso));
    if (!novos.length) return;

    const novaLista = [...processosLote, ...novos];
    if (await persistirLote(novaLista)) {
      setProcessosLote(novaLista);
    }
  };

  const limparSelecaoTabela = () => {
    setSelecionadosLote([]);
    setLimparSelecaoLote(true);
    setTimeout(() => setLimparSelecaoLote(false), 0);
  };

  const handleRemoverDoLote = async () => {
    if (!selecionadosLote.length) {
      toastAtencao("Selecione ao menos um item para remover do lote.");
      return;
    }

    const bloqueados = selecionadosLote.filter((s) =>
      [StatusPagamentoTaxa.PAGO, StatusPagamentoTaxa.MANUAL].includes(
        s.statusPagamento ?? 0,
      ),
    );
    const removiveis = selecionadosLote.filter(
      (s) => !bloqueados.some((b) => b.idProcesso === s.idProcesso),
    );

    if (bloqueados.length) {
      toastAtencao(
        "Processos pagos ou com Pagamento manual não podem ser removidos do lote.",
      );
    }
    if (!removiveis.length) return;

    const novaLista = processosLote.filter(
      (p) => !removiveis.some((s) => s.idProcesso === p.idProcesso),
    );
    limparSelecaoTabela();
    if (await persistirLote(novaLista)) {
      if (!novaLista.length) {
        navigate("/financeiro/pagamento-taxa-sp/lote");
        return;
      }
      setProcessosLote(novaLista);
    }
  };

  const recarregarLote = async () => {
    if (idLote == null) return;
    setRecarregando(true);
    try {
      const itens = await carregar(idLote);
      setProcessosLote(itens);
      setIdsOriginais(itens.map((p) => p.idProcesso));
    } finally {
      setRecarregando(false);
    }
  };

  const podeReprocessarStatus = (status?: number | null) =>
    (status ?? StatusPagamentoTaxa.PENDENTE) ===
    StatusPagamentoTaxa.DEBITOS_ADICIONAIS;

  const podeReprocessarLote =
    selecionadosLote.length > 0 &&
    selecionadosLote.every((p) => podeReprocessarStatus(p.statusPagamento));

  const handleReprocessarConsulta = async () => {
    if (idLote == null || loadingReprocessar) return;
    if (!podeReprocessarLote) {
      toastAtencao(
        "Selecione apenas processos com débitos adicionais para reprocessar.",
      );
      return;
    }
    try {
      await reprocessarLote(
        idLote,
        selecionadosLote.map((p) => p.idProcesso),
      );
      await recarregarLote();
      setLimparSelecaoLote(true);
      setTimeout(() => setLimparSelecaoLote(false), 0);
      toastSucesso("Consulta reprocessada com sucesso.");
    } catch {
      toastErro("Não foi possível reprocessar a consulta. Tente novamente.");
    }
  };

  const handleReprocessarProcesso = async (processo: IProcessoTaxa) => {
    if (idLote == null || loadingReprocessar) return;
    if (!podeReprocessarStatus(processo.statusPagamento)) {
      toastAtencao(
        "Só é possível reprocessar processos com débitos adicionais.",
      );
      return;
    }
    try {
      await reprocessar(idLote, processo.idProcesso);
      await recarregarLote();
      toastSucesso("Consulta reprocessada com sucesso.");
    } catch {
      toastErro("Não foi possível reprocessar a consulta. Tente novamente.");
    }
  };

  const handlePagarEmLote = () => {
    if (!podePagar) {
      toastAtencao("Você não tem permissão para realizar pagamentos.");
      return;
    }
    if (!selecionadosPagaveis.length) {
      toastAtencao(
        "Selecione ao menos um processo elegível. Pagos, pagamento manual e cancelados não podem ser pagos.",
      );
      return;
    }
    setProcessosParaPagar(selecionadosPagaveis);
    setPagamentoSucesso(false);
    setShowModalPagar(true);
  };

  const houvePagamento = (resultado: any): boolean => {
    const totaisPorStatus = resultado?.totaisPorStatus;
    if (!totaisPorStatus) return true;

    if (totaisPorStatus.pago === 0) {
      const motivo = resultado?.resultados?.find(
        (r: any) => r.motivoErro,
      )?.motivoErro;
      toastErro(motivo ?? "Não foi possível efetuar o pagamento. Tente novamente.");
      return false;
    }

    if (totaisPorStatus.erro > 0) {
      toastAtencao(
        `${totaisPorStatus.pago} pago(s), ${totaisPorStatus.erro} com erro. Verifique os processos com erro.`,
      );
    }
    return true;
  };

  const handleConfirmarPagamento = async () => {
    if (idLote == null || pagando || !processosParaPagar.length) return;
    try {
      const resultado = await pagarLote(
        idLote,
        processosParaPagar.map((p) => p.idProcesso),
        true,
      );
      await recarregarLote();
      limparSelecaoTabela();

      if (!houvePagamento(resultado)) {
        setShowModalPagar(false);
        setPagamentoSucesso(false);
        return;
      }
      setPagamentoSucesso(true);
    } catch (error: any) {
      toastErro(
        error?.response?.status < 500 && error?.response?.data?.message
          ? error.response.data.message
          : "Não foi possível efetuar o pagamento. Tente novamente.",
      );
    }
  };

  const handleFecharModalPagar = () => {
    setShowModalPagar(false);
    setPagamentoSucesso(false);
    setProcessosParaPagar([]);
  };

  const handlePagarDebito = async (processo: IProcessoTaxa) => {
    if (idLote == null || pagando) return;
    if (!podePagar) {
      toastAtencao("Você não tem permissão para realizar pagamentos.");
      return;
    }

    const temDebitosAdicionais =
      (processo.statusPagamento ?? StatusPagamentoTaxa.PENDENTE) ===
      StatusPagamentoTaxa.DEBITOS_ADICIONAIS;

    if (temDebitosAdicionais) {
      setProcessosParaPagar([processo]);
      setPagamentoSucesso(false);
      setShowModalPagar(true);
      return;
    }

    try {
      const resultado = await pagarLote(idLote, [processo.idProcesso]);
      await recarregarLote();
      limparSelecaoTabela();
      houvePagamento(resultado);
    } catch (error: any) {
      toastErro(
        error?.response?.status < 500 && error?.response?.data?.message
          ? error.response.data.message
          : "Não foi possível efetuar o pagamento. Tente novamente.",
      );
    }
  };

  const handleConfigColunas = () => {
    setShowModalColunas(true);
  };

  const handleExportar = () => {
    if (!processosFiltrados.length) return;

    const labelStatus = (status?: number | null) =>
      statusPagamentoTaxaOptions.find((o) => o.value === status)?.label ??
      "Pendente";

    const formatarDebitos = (p: IProcessoTaxa) =>
      p.detalheDebitos?.length
        ? p.detalheDebitos
            .map((d) => `${d.tipo}: ${formatarDinheiro(d.valor)}`)
            .join("; ")
        : "-";

    const dados = processosFiltrados.map((p) => {
      const status = p.statusPagamento ?? StatusPagamentoTaxa.PENDENTE;
      return {
        "ID processo": p.idProcesso,
        Placa: p.placa ?? "-",
        Renavam: p.renavam ?? "-",
        Empresa: p.empresa ?? "-",
        "Cliente final": p.clienteFinal ?? "-",
        "CPF/CNPJ": p.cpfCnpj ?? "-",
        UF: p.uf ?? "-",
        "Tipo de pgto": p.tipoPgto ?? "-",
        Status: labelStatus(p.statusPagamento),
        Valor: p.valor != null ? formatarDinheiro(p.valor) : "-",
        Pagamento: p.dataHoraPagamento
          ? converterUtcParaBr(p.dataHoraPagamento)
          : "-",
        Observação:
          status === StatusPagamentoTaxa.MANUAL ||
          status === StatusPagamentoTaxa.CANCELADO ||
          status === StatusPagamentoTaxa.ERRO
            ? (p.observacao ?? "-")
            : "-",
        "Detalhe dos débitos": formatarDebitos(p),
      };
    });

    const planilha = utils.json_to_sheet(dados);
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha, "Itens do lote");
    writeFileXLSX(workBook, "Pagamento_Taxa_SP_itens.xlsx");
  };

  const handleBaixarComprovante = async (processo: IProcessoTaxa) => {
    if (idLote == null) return;
    try {
      await baixarComprovante(idLote, processo.idProcesso);
    } catch {
      toastErro("Não foi possível baixar o comprovante. Tente novamente.");
    }
  };

  const handleAbrirBaixaManual = (processo: IProcessoTaxa) => {
    setProcessoBaixa(processo);
    setShowModalBaixa(true);
  };

  const handleConfirmarBaixaManual = async (
    dataPagamento: string,
    comprovante: File,
    observacao: string,
    valorPago: number,
  ) => {
    if (idLote == null || !processoBaixa) return;
    try {
      await pagarManual(
        idLote,
        processoBaixa.idProcesso,
        dataPagamento,
        comprovante,
        observacao,
        valorPago,
      );
      await recarregarLote();
      toastSucesso("Pagamento manual registrado com sucesso.");
    } catch {
      toastErro("Não foi possível registrar o pagamento manual. Tente novamente.");
    }
  };

  const handleCancelarProcesso = async (observacao: string) => {
    if (idLote == null || !processoBaixa) return;
    try {
      await cancelar(idLote, processoBaixa.idProcesso, observacao);
      await recarregarLote();
      toastSucesso("Processo cancelado com sucesso.");
    } catch {
      toastErro("Não foi possível cancelar o processo. Tente novamente.");
    }
  };

  const popoverDoStatus = (row: IProcessoTaxa): ReactNode => {
    const s = row.statusPagamento ?? StatusPagamentoTaxa.PENDENTE;

    if (s === StatusPagamentoTaxa.DEBITOS_ADICIONAIS) {
      return (
        <PopoverDebitosAdicionais
          placa={row.placa}
          debitos={(row.detalheDebitos ?? []).filter(
            (d) => d.tipo.toLowerCase() !== "taxa transferência",
          )}
        />
      );
    }

    if (
      [
        StatusPagamentoTaxa.MANUAL,
        StatusPagamentoTaxa.CANCELADO,
        StatusPagamentoTaxa.ERRO,
      ].includes(s)
    ) {
      return (
        <PopoverObservacao
          titulo={StatusPagamentoTaxaTituloPopover[s] ?? StatusPagamentoTaxaLabel[s]}
          mensagem={row.observacao?.trim() || "Sem informações adicionais."}
        />
      );
    }

    return null;
  };

  const colunasLote: IColum<IProcessoTaxa>[] = [
    {
      name: "ID",
      selector: ({ idProcesso }) => idProcesso,
      sortable: true,
      sortField: "idProcesso",
      width: "90px",
      cell: ({ idProcesso }) => (
        <span
          onClick={() => redirecionarProcesso(idProcesso)}
          style={{ color: "#1F6FEB", cursor: "pointer", textDecoration: "underline" }}
        >
          {idProcesso}
        </span>
      ),
    },
    {
      name: "Placa",
      selector: ({ placa }) => placa ?? "-",
      sortable: true,
      sortField: "placa",
      width: "100px",
      omit: !isVisible("placa"),
    },
    {
      name: "Renavam",
      selector: ({ renavam }) => renavam ?? "-",
      sortable: true,
      sortField: "renavam",
      width: "120px",
      omit: !isVisible("renavam"),
    },
    {
      name: "Empresa",
      selector: ({ empresa }) => empresa ?? "-",
      sortable: true,
      sortField: "empresa",
      width: "200px",
      omit: !isVisible("empresa"),
    },
    {
      name: "Cliente final",
      selector: ({ clienteFinal }) => clienteFinal ?? "-",
      sortable: true,
      sortField: "clienteFinal",
      width: "170px",
      omit: !isVisible("clienteFinal"),
    },
    {
      name: "CPF/CNPJ",
      selector: ({ cpfCnpj }) => cpfCnpj ?? "-",
      sortable: true,
      sortField: "cpfCnpj",
      width: "120px",
      omit: !isVisible("cpfCnpj"),
    },
    {
      name: "UF Origem",
      selector: ({ uf }) => uf ?? "-",
      sortable: true,
      sortField: "uf",
      width: "110px",
      omit: !isVisible("uf"),
    },
    {
      name: "Tipo de pgto",
      selector: ({ tipoPgto }) => tipoPgto ?? "-",
      cell: (row) =>
        semOrigemVeiculo(row) ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                <FaExclamationTriangle
                  style={{ marginRight: 4, color: "#fe8f14" }}
                />
                Não identificado UF e Município de origem do veículo
              </Tooltip>
            }
          >
            <span style={{ display: "inline-flex", cursor: "pointer" }}>
              <FaExclamationTriangle color="#FF9F42" size={15} />
            </span>
          </OverlayTrigger>
        ) : (
          row.tipoPgto ?? "-"
        ),
      sortable: true,
      sortField: "tipoPgto",
      width: "150px",
      omit: !isVisible("tipoPgto"),
    },
    {
      name: "Status",
      selector: ({ statusPagamento }) => statusPagamento ?? 0,
      cell: (row) => (
        <StatusItemPagamento
          status={row.statusPagamento}
          popover={popoverDoStatus(row)}
        />
      ),
      sortable: true,
      sortField: "statusPagamento",
      width: "140px",
      omit: !isVisible("status"),
    },
    {
      name: "Valor",
      selector: ({ valor }) => (valor != null ? formatarDinheiro(valor) : "-"),
      cell: (row) => {
        const texto = row.valor != null ? formatarDinheiro(row.valor) : "-";
        const debitosAdicionais = (row.detalheDebitos ?? []).filter(
          (d) => d.tipo.toLowerCase() !== "taxa transferência",
        );
        if (
          (row.statusPagamento ?? StatusPagamentoTaxa.PENDENTE) ===
            StatusPagamentoTaxa.PAGO &&
          debitosAdicionais.length
        ) {
          return (
            <PopoverHover
              iconSize={13}
              popover={
                <PopoverDebitosAdicionais
                  placa={row.placa}
                  debitos={debitosAdicionais}
                  pago
                />
              }
            >
              <span>{texto}</span>
            </PopoverHover>
          );
        }
        return texto;
      },
      sortable: true,
      sortField: "valor",
      width: "120px",
      omit: !isVisible("valor"),
    },
    {
      name: "Pagamento",
      selector: ({ dataHoraPagamento }) =>
        dataHoraPagamento ? converterUtcParaBr(dataHoraPagamento) : "-",
      sortable: true,
      sortField: "dataHoraPagamento",
      width: "180px",
      omit: !isVisible("pagamento"),
    },
    {
      name: "Ações",
      selector: () => "",
      cell: (row) => {
        const finalizado = [
          StatusPagamentoTaxa.PAGO,
          StatusPagamentoTaxa.MANUAL,
          StatusPagamentoTaxa.CANCELADO,
        ].includes(row.statusPagamento ?? StatusPagamentoTaxa.PENDENTE);
        const podeBaixar = idsOriginais.includes(row.idProcesso) && !finalizado;
        const podePagarDebito = !finalizado && podePagar;
        const possuiComprovante = !!row.idImagem;
        const podeReprocessar = podeReprocessarStatus(row.statusPagamento);
        return (
          <div style={{ display: "flex", gap: 14 }}>
            <FaDollarSign
              size={15}
              title={
                finalizado
                  ? "Processo já finalizado"
                  : podePagar
                    ? "Pagar débito"
                    : "Você não tem permissão para realizar pagamentos."
              }
              onClick={podePagarDebito ? () => handlePagarDebito(row) : undefined}
              style={{
                cursor: podePagarDebito ? "pointer" : "not-allowed",
                color: podePagarDebito ? "#555" : "#C9C9C9",
              }}
            />
            <FaFileInvoiceDollar
              size={15}
              title={
                podeBaixar
                  ? "Baixa manual / cancelar"
                  : finalizado
                    ? "Processo já finalizado"
                    : "Salve o lote antes de dar baixa neste processo"
              }
              onClick={podeBaixar ? () => handleAbrirBaixaManual(row) : undefined}
              style={{
                cursor: podeBaixar ? "pointer" : "not-allowed",
                color: podeBaixar ? "#555" : "#C9C9C9",
              }}
            />
            <FaSyncAlt
              size={15}
              title={
                podeReprocessar
                  ? "Reprocessar consulta"
                  : "Só processos com débitos adicionais podem ser reprocessados"
              }
              onClick={
                podeReprocessar
                  ? () => handleReprocessarProcesso(row)
                  : undefined
              }
              style={{
                cursor: podeReprocessar ? "pointer" : "not-allowed",
                color: podeReprocessar ? "#555" : "#C9C9C9",
              }}
            />
            <FaFileDownload
              size={15}
              title={
                possuiComprovante
                  ? "Baixar comprovante"
                  : "Sem comprovante para este processo"
              }
              onClick={
                possuiComprovante
                  ? () => handleBaixarComprovante(row)
                  : undefined
              }
              style={{
                cursor: possuiComprovante ? "pointer" : "not-allowed",
                color: possuiComprovante ? "#555" : "#C9C9C9",
              }}
            />
          </div>
        );
      },
      width: "150px",
    },
  ];

  return (
    <PageV2
      title="Pagamento de Taxas SP"
      lastPage="Financeiro"
      redirectDoIcone="/financeiro/pagamento-taxa-sp/lote"
    >
        <LoadingScreen
          mostrar={
            carregandoProcessos ||
            salvando ||
            salvandoBaixa ||
            loadingReprocessar ||
            pagando ||
            recarregando ||
            filtrando
          }
        />
        <Card>
          <Header>
            <Title>{titulo}</Title>
            <HeaderBotoes>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                onClick={handleCancelar}
                style={{ fontSize: 12 }}
              >
                Cancelar
              </PrimaryButton>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                onClick={handleRemoverDoLote}
                disabled={!selecionadosLote.length}
                title={
                  !selecionadosLote.length
                    ? "Selecione ao menos um processo para remover."
                    : undefined
                }
                style={{ fontSize: 12 }}
              >
                <FaTrash /> Remover do lote
              </PrimaryButton>
              <PrimaryButton
                type="button"
                variante="black"
                onClick={handleSelecionarProcessos}
                disabled={loteFinalizado}
                title={
                  loteFinalizado
                    ? "Lote finalizado: não é possível adicionar novos processos."
                    : undefined
                }
                style={{ fontSize: 12 }}
              >
                <FaPlus /> Selecionar processos
              </PrimaryButton>
            </HeaderBotoes>
          </Header>

          <FormContainer
            style={{
              pointerEvents: possuiItensNoLote ? "auto" : "none",
              opacity: possuiItensNoLote ? 1 : 0.5,
            }}
            aria-disabled={!possuiItensNoLote}
          >
            <Form noValidate onSubmit={handleSubmit(handleBuscar)}>
              <Row>
                <InputContainer lg="3">
                  <Form.Group>
                    <Form.Label>Empresa</Form.Label>
                    <Controller
                      control={control}
                      name="idEmpresa"
                      render={({ field: { onChange, value } }) => (
                        <DropdownSelect
                          title=""
                          options={empresasLoteOptions}
                          selectedValue={value ?? undefined}
                          onSelect={onChange}
                          searchable
                        />
                      )}
                    />
                  </Form.Group>
                </InputContainer>

                <InputContainer lg="2">
                  <Form.Group>
                    <Form.Label>Veículo</Form.Label>
                    <Controller
                      control={control}
                      name="dadosVeiculos"
                      defaultValue={[]}
                      render={({ field: { onChange, value } }) => (
                        <CustomInputModal
                          label="Filtrar em massa"
                          value={value ?? []}
                          placeholder="Placa, chassi ou renavam"
                          onConfirm={onChange}
                          validar={false}
                          style={{ width: "100%" }}
                          placeholderModal="Digite ou cole placa, chassi ou renavam e tecle ENTER"
                          labelItem="Veículos"
                        />
                      )}
                    />
                  </Form.Group>
                </InputContainer>

                <InputContainer lg="2">
                  <Form.Group>
                    <Form.Label>Período de pagamento</Form.Label>
                    <DatePickerContainer>
                      <CustomDatePicker
                        selected={inicioPagamento}
                        onChange={(dates: any) => {
                          const [start, end] = dates;
                          setInicioPagamento(start);
                          setFimPagamento(end);
                        }}
                        startDate={inicioPagamento}
                        endDate={fimPagamento}
                        rangeSelect={true}
                      />
                    </DatePickerContainer>
                  </Form.Group>
                </InputContainer>

                <InputContainer lg="2">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Controller
                      control={control}
                      name="status"
                      render={({ field: { onChange, value } }) => (
                        <DropdownSelect
                          title=""
                          options={statusPagamentoTaxaOptions}
                          selectedValue={value ?? undefined}
                          onSelect={onChange}
                        />
                      )}
                    />
                  </Form.Group>
                </InputContainer>

                <InputContainer lg="3">
                  <Form.Group>
                    <AcoesFiltroContainer>
                      <AcoesContainer>
                        <Button
                          type="reset"
                          outline
                          variante="primary"
                          onClick={handleLimpar}
                        >
                          Limpar
                        </Button>
                        <Button type="submit">Buscar</Button>
                      </AcoesContainer>
                      <MaisFiltros
                        handleClickAplicarFiltros={handleSubmit(handleBuscar)}
                        limparTudoOnClick={handleLimpar}
                        cancelarOnClick={handleLimpar}
                      >
                        <InputContainerMaisFiltros>
                          <Form.Label>Processo</Form.Label>
                          <Controller
                            control={control}
                            name="processo"
                            render={({ field: { onChange, value } }) => (
                              <InputBuscaProcesso>
                                <input
                                  value={value ?? ""}
                                  onChange={(e) => onChange(e.target.value)}
                                  placeholder="Busque pelo processo"
                                />
                                <FaSearch size={13} />
                              </InputBuscaProcesso>
                            )}
                          />
                        </InputContainerMaisFiltros>

                        <InputContainerMaisFiltros>
                          <Form.Label>Cliente final</Form.Label>
                          <Controller
                            control={control}
                            name="clienteFinal"
                            render={({ field: { onChange, value } }) => (
                              <DropdownSelect
                                title=""
                                options={clientesFinaisOptions}
                                selectedValue={value ?? undefined}
                                onSelect={onChange}
                                searchable
                              />
                            )}
                          />
                        </InputContainerMaisFiltros>

                        <InputContainerMaisFiltros>
                          <Form.Label>Tipo de pagamento</Form.Label>
                          <Controller
                            control={control}
                            name="tipoPagamento"
                            render={({ field: { onChange, value } }) => (
                              <DropdownSelect
                                title=""
                                options={tipoPagamentoOptions}
                                selectedValue={value ?? undefined}
                                onSelect={onChange}
                              />
                            )}
                          />
                        </InputContainerMaisFiltros>
                      </MaisFiltros>
                    </AcoesFiltroContainer>
                  </Form.Group>
                </InputContainer>
              </Row>
            </Form>
          </FormContainer>
        </Card>

        <Card>
          <Header>
            <Title>Itens para pagamento</Title>
            <HeaderBotoes>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                onClick={handleExportar}
                disabled={!possuiItensNoLote || idLote == null}
                title={
                  idLote == null
                    ? "Salve o lote antes de exportar."
                    : "Exportar Excel"
                }
                style={{ fontSize: 12 }}
              >
                <FaUpload />
              </PrimaryButton>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                onClick={handleConfigColunas}
                disabled={!possuiItensNoLote}
                title="Configurar colunas"
                style={{ fontSize: 12 }}
              >
                <FaColumns />
              </PrimaryButton>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                onClick={handleReprocessarConsulta}
                disabled={!podeReprocessarLote || loadingReprocessar}
                title={
                  podeReprocessarLote
                    ? "Reprocessar consulta dos selecionados"
                    : "Selecione apenas processos com débitos adicionais"
                }
                style={{ fontSize: 12 }}
              >
                <FaSyncAlt /> Reprocessar consulta
              </PrimaryButton>
              <PrimaryButton
                type="button"
                variante="black"
                onClick={handlePagarEmLote}
                disabled={idLote == null || !podePagar || !selecionadosPagaveis.length}
                title={
                  idLote == null
                    ? "Salve o lote antes de realizar o pagamento."
                    : !podePagar
                      ? "Você não tem permissão para realizar pagamentos."
                      : !selecionadosPagaveis.length
                        ? "Selecione processos elegíveis para pagamento."
                        : undefined
                }
                style={{ fontSize: 12 }}
              >
                <FaDollarSign /> Pagar em lote
              </PrimaryButton>
            </HeaderBotoes>
          </Header>
          {carregandoProcessos ? null : possuiItensNoLote ? (
            <TabelaContainer>
              <Table
                key={versaoBuscaLote}
                keyField="idProcesso"
                columns={colunasLote}
                data={dadosTabelaLote}
                totalRows={dadosTabelaLote.length}
                pagination={true}
                paginationRowsPerPageOptions={[20, 50, 100]}
                rowsPerPage={50}
                loading={false}
                noSubHeader
                possuiTooltip={false}
                selectableRows
                setRowsSelected={setSelecionadosLote}
                clearSelectedRows={limparSelecaoLote}
                handleSortFunction={handleOrdenar}
              />
            </TabelaContainer>
          ) : (
            <Placeholder>
              Selecione os processos que deseja adicionar a este lote de pagamento.
            </Placeholder>
          )}
        </Card>

      <ModalSelecionarProcessos
        show={showModalProcessos}
        onClose={() => setShowModalProcessos(false)}
        onAdicionar={handleAdicionarProcessos}
        idsNoLote={idsNoLote}
      />

      <ModalConfigColunas
        show={showModalColunas}
        handleClose={() => setShowModalColunas(false)}
        configColunas={configColunas}
        onSave={salvarConfigColunas}
        labels={colunaLabelsLote}
      />

      <ModalConfirmarPagamento
        show={showModalPagar}
        quantidade={processosParaPagar.length}
        possuiDebitosAdicionais={processosParaPagar.some(
          (p) =>
            (p.statusPagamento ?? StatusPagamentoTaxa.PENDENTE) ===
            StatusPagamentoTaxa.DEBITOS_ADICIONAIS,
        )}
        sucesso={pagamentoSucesso}
        loading={pagando}
        handleClose={handleFecharModalPagar}
        onConfirm={handleConfirmarPagamento}
      />

      <ModalBaixaManual
        show={showModalBaixa}
        titulo={
          processoBaixa
            ? `Baixa manual — processo ${processoBaixa.idProcesso}`
            : "Baixa manual"
        }
        alertaManual="Os custos e valores a receber deverão ser lançados manualmente no processo."
        handleClose={() => setShowModalBaixa(false)}
        onConfirmarManual={handleConfirmarBaixaManual}
        onCancelar={handleCancelarProcesso}
      />
    </PageV2>
  );
}
